import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { getSocket } from "../../services/socket-io";

import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import { pooledModeFromSetting as mapPooledMode } from "../../helpers/canOpenTicket";
import { AuthContext } from "../../context/Auth/AuthContext";
import ContactTypeModal from "../../components/ContactTypeModal";
import useStyles from "./styles";
import { DEFAULT_MEDIA_ACTIONS, deriveMediaActions } from "./mediaActions";
import TabNegocio from "./tabs/TabNegocio";
import TabBandeja from "./tabs/TabBandeja";
import TabMensajes from "./tabs/TabMensajes";
import TabAutomatizaciones from "./tabs/TabAutomatizaciones";
import TabReporteEmail from "./tabs/TabReporteEmail";
import TabClasificacion from "./tabs/TabClasificacion";
import TabIntegraciones from "./tabs/TabIntegraciones";
import TabPermisos from "./tabs/TabPermisos";

// feat_347 — los 8 tabs de /Settings.
// feat_352 (Fase 2, paso 2) — siguen siendo 8, pero cambió la lista: se fue "ia"
// (sus 3 bloques se fusionaron en "automatizaciones", que ahora tiene los gates
// de la misma IA al lado de los 3 escenarios de auto-cierre) y entró
// "reporte-email" (salió del final de "automatizaciones", que quedaba con 11
// bloques). NO se agregó alias para el id viejo "ia": nunca se compartió un link
// /settings?tab=ia, y un id inválido ya cae al DEFAULT_TAB desde feat_347, que es
// el comportamiento correcto.
//
// 🔴 EL RUTEO ES POR QUERY PARAM (`/settings?tab=negocio`), NUNCA por rutas hijas.
// `routes/Route.js:14` tiene "/Settings" en ADMIN_ONLY_PATHS y compara STRING
// EXACTO para redirigir al superadmin a /superadmin. Si el ruteo de tabs alterara
// el PATH, ese guard dejaría de matchear y el superadmin entraría a una pantalla
// que hoy tiene prohibida. El path NO se toca: sólo el query string.
//
// Los ids son los slugs de la URL (minúscula, sin acentos). `labelKey` va
// explícito y NO se arma por template string: una key i18n construida en runtime
// no se puede auditar con un grep.
const TABS = [
	{ id: "negocio", labelKey: "settingsExtra.tabNegocio", Component: TabNegocio },
	{ id: "bandeja", labelKey: "settingsExtra.tabBandeja", Component: TabBandeja },
	{ id: "mensajes", labelKey: "settingsExtra.tabMensajes", Component: TabMensajes },
	{ id: "automatizaciones", labelKey: "settingsExtra.tabAutomatizaciones", Component: TabAutomatizaciones },
	{ id: "reporte-email", labelKey: "settingsExtra.tabReporteEmail", Component: TabReporteEmail },
	{ id: "clasificacion", labelKey: "settingsExtra.tabClasificacion", Component: TabClasificacion },
	{ id: "integraciones", labelKey: "settingsExtra.tabIntegraciones", Component: TabIntegraciones },
	{ id: "permisos", labelKey: "settingsExtra.tabPermisos", Component: TabPermisos },
];
const TAB_IDS = TABS.map(t => t.id);
const DEFAULT_TAB = "negocio";

const getWebhookFields = () => [
	{ key: "webhookUrl", label: i18n.t("settingsExtra.urlAutomatizacion") },
	{ key: "webhookToken", label: i18n.t("settingsExtra.tokenAutomatizacion") },
	{ key: "webhookTimeout", label: i18n.t("settingsExtra.timeoutSegundos"), type: "number", inputProps: { min: 1, max: 120 }, defaultValue: "10" },
	{ key: "webhookRetries", label: i18n.t("settingsExtra.reintentos"), type: "number", inputProps: { min: 0, max: 10 }, defaultValue: "2" },
	{ key: "webhookPrompt", label: i18n.t("settingsExtra.promptAsistente"), multiline: true, rows: 6 },
];

const Settings = () => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const history = useHistory();
	const location = useLocation();

	// feat_347 — tab activo. Se inicializa desde `?tab=` para que el deep-link y el
	// F5 caigan en el tab correcto. Un valor ausente o inválido cae a DEFAULT_TAB.
	const [activeTab, setActiveTab] = useState(() => {
		const raw = new URLSearchParams(location.search).get("tab");
		return TAB_IDS.includes(raw) ? raw : DEFAULT_TAB;
	});

	const [settings, setSettings] = useState([]);
	const [webhookUrl, setWebhookUrl] = useState("");
	const [webhookToken, setWebhookToken] = useState("");
	const [webhookTimeout, setWebhookTimeout] = useState("10");
	const [webhookRetries, setWebhookRetries] = useState("2");
	const [webhookPrompt, setWebhookPrompt] = useState("");
	const [iaEnabled, setIaEnabled] = useState(false);
	const [autoCloseEnabled, setAutoCloseEnabled] = useState(false);
	const [autoCloseHours, setAutoCloseHours] = useState("24");
	const [autoCloseNotifyMinutes, setAutoCloseNotifyMinutes] = useState("0");
	const [autoCloseFarewellMessage, setAutoCloseFarewellMessage] = useState("");
	const [autoCloseNotifyAI, setAutoCloseNotifyAI] = useState(false);
	// 3-scenario auto-close
	const [cartAbandonedEnabled, setCartAbandonedEnabled] = useState(false);
	const [cartAbandonedHours, setCartAbandonedHours] = useState("4");
	const [cartAbandonedScheduleMode, setCartAbandonedScheduleMode] = useState("always");
	const [cartAbandonedSchedule, setCartAbandonedSchedule] = useState({});
	const [always247, setAlways247] = useState(false);
	const [businessHours, setBusinessHours] = useState({});
	// feat_289: datos del negocio (payload webhook IA) + timezone
	const [businessAddress, setBusinessAddress] = useState("");
	const [businessPhone, setBusinessPhone] = useState("");
	const [businessWhatsapp, setBusinessWhatsapp] = useState("");
	const [businessEmail, setBusinessEmail] = useState("");
	const [businessLocationLink, setBusinessLocationLink] = useState("");
	const [businessHoursText, setBusinessHoursText] = useState("");
	const [businessTimezone, setBusinessTimezone] = useState("America/Argentina/Cordoba");
	const [unansweredEnabled, setUnansweredEnabled] = useState(false);
	const [unansweredHours, setUnansweredHours] = useState("2");
	const [forgottenEnabled, setForgottenEnabled] = useState(false);
	const [forgottenHours, setForgottenHours] = useState("24");
	const [contactTypes, setContactTypes] = useState([]);
	const [ctForm, setCtForm] = useState({ name: "", color: "#667eea" });
	const [contactReasons, setContactReasons] = useState([]);
	const [crForm, setCrForm] = useState({ name: "" });
	// TDR thresholds
	const [tdrYellowThreshold, setTdrYellowThreshold] = useState("5");
	const [tdrRedThreshold, setTdrRedThreshold] = useState("10");
	// Vendedor IA
	const [vendedorIaEnabled, setVendedorIaEnabled] = useState(true);
	const [vendedorIaSuperadminEnabled, setVendedorIaSuperadminEnabled] = useState(true);
	// Secretaria IA + admin control (feat_181)
	const [secretariaIaEnabled, setSecretariaIaEnabled] = useState(true);
	const [iaAdminCanToggle, setIaAdminCanToggle] = useState(false);
	// feat_288: capa operativa (read-only en Settings) + timer horario
	const [secretariaIaActive, setSecretariaIaActive] = useState(true);
	const [vendedorIaActive, setVendedorIaActive] = useState(true);
	const [iaScheduleEnabled, setIaScheduleEnabled] = useState(false);
	// Silenciar IA al tomar el ticket (default off)
	const [aiSilenceOnTicketOpen, setAiSilenceOnTicketOpen] = useState(false);
	// Firma de mensajes
	const [signatureEnabled, setSignatureEnabled] = useState(false);
	const [signatureQuoteStyle, setSignatureQuoteStyle] = useState(false);
	const [cancelScheduledOnReply, setCancelScheduledOnReply] = useState(true);
	const [outboundTypingEnabled, setOutboundTypingEnabled] = useState(true); // feat_177
	const [outboundTypingAiEnabled, setOutboundTypingAiEnabled] = useState(true); // feat_178
	const [markReadOnOpen, setMarkReadOnOpen] = useState(false); // markReadOnOpen default OFF
	const [showUserFilter, setShowUserFilter] = useState(false); // feat_301 default OFF
	const [inboxMode, setInboxMode] = useState("split"); // feat_323 default split (comportamiento actual)
	const [attendingMode, setAttendingMode] = useState("off"); // feat_295: off | visual | lock
	const [directResolve, setDirectResolve] = useState(false); // feat_343 default OFF
	const [closedPooledMode, setClosedPooledMode] = useState("off"); // feat_338: off | queue | company
	// Auto-reply to incoming media
	const [autoReplyMediaEnabled, setAutoReplyMediaEnabled] = useState(false);
	const [autoReplyMediaScope, setAutoReplyMediaScope] = useState("ai_only");
	const [autoReplyMediaActions, setAutoReplyMediaActions] = useState({ ...DEFAULT_MEDIA_ACTIONS });
	const [autoReplyMediaTexts, setAutoReplyMediaTexts] = useState({});
	const [autoReplyMediaTimeout, setAutoReplyMediaTimeout] = useState("60");
	// Modal de edición de tipo de cliente
	const [editingContactType, setEditingContactType] = useState(null);
	const [typeModalOpen, setTypeModalOpen] = useState(false);
	// Ventana de reapertura de tickets
	const [ticketReopenWindowDays, setTicketReopenWindowDays] = useState("1");
	const [ticketReopenWindowMax, setTicketReopenWindowMax] = useState(null);
	// Identidad del asistente
	const [businessName, setBusinessName] = useState("");
	const [sede1, setSede1] = useState("");
	const [sede2, setSede2] = useState("");
	const [assistantName, setAssistantName] = useState("Asistente");
	const [language, setLanguage] = useState("es");
	const [tone, setTone] = useState("informal");

	// Permisos de usuarios
	const [userCanDeleteTicket, setUserCanDeleteTicket] = useState(false);
	const [userCanDeleteMessage, setUserCanDeleteMessage] = useState(false);
	// Email report settings
	const [emailReportEnabled, setEmailReportEnabled] = useState(false);
	const [emailReportRecipients, setEmailReportRecipients] = useState("");
	const [emailReportTriggerQueueIds, setEmailReportTriggerQueueIds] = useState([]);
	const [emailReportTimeoutHours, setEmailReportTimeoutHours] = useState("2");
	const [emailReportTimerMode, setEmailReportTimerMode] = useState("updatedAt");
	const [emailReportSubjectTemplate, setEmailReportSubjectTemplate] = useState("");
	const [emailReportHtmlTemplate, setEmailReportHtmlTemplate] = useState("");
	const [queues, setQueues] = useState([]);
	const [emailTestRecipient, setEmailTestRecipient] = useState("");
	const [emailTestLoading, setEmailTestLoading] = useState(false);
	const [emailTestResult, setEmailTestResult] = useState(null);

	// clientVisibleFields: which webhook fields the company admin configured as visible
	const [clientVisibleFields, setClientVisibleFields] = useState(null); // null = not loaded yet
	const [showApiToken, setShowApiToken] = useState(false);

	// feat_339: gate de carga. Arranca en true para que la página NUNCA muestre el
	// formulario con los valores iniciales de useState ("", false, "24"...) — eso era
	// lo que se veía como "los campos aparecen vacíos unos segundos".
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);

				const getValue = key => {
					const found = data.find(s => s.key === key);
					return found ? found.value : "";
				};

				setWebhookUrl(getValue("webhookUrl"));
				setWebhookToken(getValue("webhookToken"));
				setWebhookTimeout(getValue("webhookTimeout") || "10");
				setWebhookRetries(getValue("webhookRetries") || "2");
				setWebhookPrompt(getValue("webhookPrompt"));
				setIaEnabled(getValue("iaEnabled") === "true");
				setAutoCloseEnabled(getValue("autoCloseInactiveTickets") === "enabled");
				setAutoCloseHours(getValue("autoCloseInactiveHours") || "24");
				setAutoCloseNotifyMinutes(getValue("autoCloseNotifyMinutes") || "0");
				setAutoCloseFarewellMessage(getValue("autoCloseFarewellMessage") || "");
				setAutoCloseNotifyAI(getValue("autoCloseNotifyAI") === "true");
				setCartAbandonedEnabled(getValue("autoCloseCartAbandonedEnabled") === "true");
				setCartAbandonedHours(getValue("autoCloseCartAbandonedHours") || "4");
				setCartAbandonedScheduleMode(getValue("autoCloseCartAbandonedScheduleMode") || "always");
				try {
					const raw = getValue("autoCloseCartAbandonedSchedule");
					setCartAbandonedSchedule(raw ? JSON.parse(raw) : {});
				} catch { setCartAbandonedSchedule({}); }
				setAlways247(getValue("always247") === "true");
				try {
					const rawBh = getValue("businessHours");
					setBusinessHours(rawBh ? JSON.parse(rawBh) : {});
				} catch { setBusinessHours({}); }
				setBusinessAddress(getValue("businessAddress") || "");
				setBusinessPhone(getValue("businessPhone") || "");
				setBusinessWhatsapp(getValue("businessWhatsapp") || "");
				setBusinessEmail(getValue("businessEmail") || "");
				setBusinessLocationLink(getValue("businessLocationLink") || "");
				setBusinessHoursText(getValue("businessHoursText") || "");
				setBusinessTimezone(getValue("businessTimezone") || "America/Argentina/Cordoba");
				setUnansweredEnabled(getValue("autoCloseUnansweredEnabled") === "true");
				setUnansweredHours(getValue("autoCloseUnansweredHours") || "2");
				setForgottenEnabled(getValue("autoCloseForgottenEnabled") === "true");
				setForgottenHours(getValue("autoCloseForgottenHours") || "24");
				setTdrYellowThreshold(getValue("tdrYellowThreshold") || "5");
				setTdrRedThreshold(getValue("tdrRedThreshold") || "10");
				setVendedorIaEnabled(getValue("vendedorIaEnabled") !== "false");
				setVendedorIaSuperadminEnabled(getValue("vendedorIaSuperadminEnabled") !== "false");
				setSecretariaIaEnabled(getValue("secretariaIaEnabled") !== "false");
				setIaAdminCanToggle(getValue("iaAdminCanToggle") === "true");
				setSecretariaIaActive(getValue("secretariaIaActive") !== "false");
				setVendedorIaActive(getValue("vendedorIaActive") !== "false");
				setIaScheduleEnabled(getValue("iaScheduleEnabled") === "true");
				setAiSilenceOnTicketOpen(getValue("aiSilenceOnTicketOpen") === "true");
				setTicketReopenWindowDays(getValue("ticketReopenWindowDays") || "1");
				setTicketReopenWindowMax(getValue("ticketReopenWindowDays") || null);
				setBusinessName(getValue("businessName") || "");
				setSede1(getValue("sede1") || "");
				setSede2(getValue("sede2") || "");
				setAssistantName(getValue("assistantName") || "Asistente");
				setLanguage(getValue("language") || "es");
				setTone(getValue("tone") || "informal");
				setUserCanDeleteTicket(getValue("userCanDeleteTicket") === "enabled");
				setUserCanDeleteMessage(getValue("userCanDeleteMessage") === "enabled");
				setSignatureEnabled(getValue("signatureEnabled") === "enabled");
				setSignatureQuoteStyle(getValue("signatureQuoteStyle") === "enabled");
				setCancelScheduledOnReply(getValue("cancelScheduledOnReply") !== "false");
				setOutboundTypingEnabled(getValue("outboundTypingEnabled") !== "false"); // feat_177 default ON
				setOutboundTypingAiEnabled(getValue("outboundTypingAiEnabled") !== "false"); // feat_178 default ON
				setMarkReadOnOpen(getValue("markReadOnOpen") === "true"); // default OFF
				setShowUserFilter(getValue("showUserFilter") === "true"); // feat_301 default OFF
				setInboxMode(getValue("inboxMode") || "split"); // feat_323 default split
				setAttendingMode(getValue("attendingMode") || "off"); // feat_295 default off
				// feat_338: alias legacy de la key booleana original ("true"→queue, "false"→off).
				setClosedPooledMode(mapPooledMode(getValue("closedTicketsPooledByQueue")));
				setDirectResolve(getValue("directResolve") === "true"); // feat_343 default OFF
				setAutoReplyMediaEnabled(getValue("autoReplyMediaEnabled") === "true");
				setAutoReplyMediaScope(getValue("autoReplyMediaScope") || "ai_only");
				setAutoReplyMediaActions(
					deriveMediaActions(getValue("autoReplyMediaActions"), getValue("autoReplyMediaTypes"))
				);
				try {
					const rawTexts = getValue("autoReplyMediaTexts");
					const parsedTexts = rawTexts ? JSON.parse(rawTexts) : {};
					setAutoReplyMediaTexts(parsedTexts && typeof parsedTexts === "object" ? parsedTexts : {});
				} catch (_) {
					setAutoReplyMediaTexts({});
				}
				setAutoReplyMediaTimeout(getValue("autoReplyMediaTimeout") || "60");
				setEmailReportEnabled(getValue("emailReportEnabled") === "true");
				setEmailReportRecipients(getValue("emailReportRecipients") || "");
				const rawQueueIds = getValue("emailReportTriggerQueueIds") || "";
				setEmailReportTriggerQueueIds(rawQueueIds ? rawQueueIds.split(",").map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n)) : []);
				setEmailReportTimeoutHours(getValue("emailReportTimeoutHours") || "2");
				setEmailReportTimerMode(getValue("emailReportTimerMode") || "updatedAt");
				const savedSubject = getValue("emailReportSubjectTemplate") || "";
				const savedHtml = getValue("emailReportHtmlTemplate") || "";
				setEmailReportSubjectTemplate(savedSubject);
				setEmailReportHtmlTemplate(savedHtml);
				if (!savedSubject || !savedHtml) {
					api.get("/settings/email-report/defaults").then(({ data }) => {
						if (!savedSubject) setEmailReportSubjectTemplate(prev => prev || data.subjectTemplate);
						if (!savedHtml) setEmailReportHtmlTemplate(prev => prev || data.htmlTemplate);
					}).catch(() => {});
				}

				api.get("/queue").then(({ data }) => setQueues(data)).catch(() => {});
				fetchContactTypes();
				api.get("/contact-reasons").then(({ data }) => setContactReasons(data)).catch(() => {});

				// Load clientVisibleFields
				const rawVisible = getValue("clientVisibleFields");
				try {
					setClientVisibleFields(rawVisible ? JSON.parse(rawVisible) : []);
				} catch (_) {
					setClientVisibleFields([]);
				}
			} catch (err) {
				toastError(err);
			} finally {
				// Solo GET /settings gatea el spinner: es el único await bloqueante y el
				// único que hidrata campos. /queue, /contact-types y /contact-reasons
				// alimentan listas del final de la página y llegan después, a propósito.
				setLoading(false);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const socket = getSocket();

		// layout (etapa 1) también escucha "settings" sobre este mismo singleton:
		// el off() va POR REFERENCIA o lo dejaría sordo.
		const handleSettings = data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex >= 0) {
						aux[settingIndex].value = data.setting.value;
					}
					return aux;
				});
				const { key, value } = data.setting;
				if (key === "webhookUrl") setWebhookUrl(value);
				if (key === "webhookToken") setWebhookToken(value);
				if (key === "webhookTimeout") setWebhookTimeout(value);
				if (key === "webhookRetries") setWebhookRetries(value);
				if (key === "webhookPrompt") setWebhookPrompt(value);
			if (key === "iaEnabled") setIaEnabled(value === "true");
			if (key === "autoCloseInactiveTickets") setAutoCloseEnabled(value === "enabled");
			if (key === "autoCloseInactiveHours") setAutoCloseHours(value);
			if (key === "autoCloseNotifyMinutes") setAutoCloseNotifyMinutes(value);
			if (key === "autoCloseFarewellMessage") setAutoCloseFarewellMessage(value);
			if (key === "autoCloseNotifyAI") setAutoCloseNotifyAI(value === "true");
			if (key === "autoCloseCartAbandonedEnabled") setCartAbandonedEnabled(value === "true");
			if (key === "autoCloseCartAbandonedHours") setCartAbandonedHours(value);
			if (key === "autoCloseCartAbandonedScheduleMode") setCartAbandonedScheduleMode(value || "always");
			if (key === "autoCloseCartAbandonedSchedule") {
				try { setCartAbandonedSchedule(value ? JSON.parse(value) : {}); } catch { setCartAbandonedSchedule({}); }
			}
			if (key === "always247") setAlways247(value === "true");
			if (key === "businessHours") {
				try { setBusinessHours(value ? JSON.parse(value) : {}); } catch { setBusinessHours({}); }
			}
			if (key === "autoCloseUnansweredEnabled") setUnansweredEnabled(value === "true");
			if (key === "autoCloseUnansweredHours") setUnansweredHours(value);
			if (key === "autoCloseForgottenEnabled") setForgottenEnabled(value === "true");
			if (key === "autoCloseForgottenHours") setForgottenHours(value);
			if (key === "tdrYellowThreshold") setTdrYellowThreshold(value);
			if (key === "tdrRedThreshold") setTdrRedThreshold(value);
			if (key === "vendedorIaEnabled") setVendedorIaEnabled(value === "true");
			if (key === "vendedorIaSuperadminEnabled") setVendedorIaSuperadminEnabled(value === "true");
			if (key === "secretariaIaEnabled") setSecretariaIaEnabled(value === "true");
			if (key === "iaAdminCanToggle") setIaAdminCanToggle(value === "true");
			if (key === "secretariaIaActive") setSecretariaIaActive(value === "true");
			if (key === "vendedorIaActive") setVendedorIaActive(value === "true");
			if (key === "iaScheduleEnabled") setIaScheduleEnabled(value === "true");
			if (key === "aiSilenceOnTicketOpen") setAiSilenceOnTicketOpen(value === "true");
			if (key === "ticketReopenWindowDays") setTicketReopenWindowDays(value);
			if (key === "businessName") setBusinessName(value);
			if (key === "sede1") setSede1(value);
			if (key === "sede2") setSede2(value);
			if (key === "assistantName") setAssistantName(value);
			if (key === "language") setLanguage(value);
			if (key === "tone") setTone(value);
			if (key === "userCanDeleteTicket") setUserCanDeleteTicket(value === "enabled");
			if (key === "userCanDeleteMessage") setUserCanDeleteMessage(value === "enabled");
			if (key === "signatureEnabled") setSignatureEnabled(value === "enabled");
			if (key === "signatureQuoteStyle") setSignatureQuoteStyle(value === "enabled");
			if (key === "cancelScheduledOnReply") setCancelScheduledOnReply(value !== "false");
			if (key === "outboundTypingEnabled") setOutboundTypingEnabled(value !== "false"); // feat_177
			if (key === "outboundTypingAiEnabled") setOutboundTypingAiEnabled(value !== "false"); // feat_178
			if (key === "markReadOnOpen") setMarkReadOnOpen(value === "true"); // default OFF
			if (key === "showUserFilter") setShowUserFilter(value === "true"); // feat_301
			if (key === "inboxMode") setInboxMode(value || "split"); // feat_323
			if (key === "attendingMode") setAttendingMode(value || "off"); // feat_295
			if (key === "closedTicketsPooledByQueue") setClosedPooledMode(mapPooledMode(value)); // feat_338
			if (key === "directResolve") setDirectResolve(value === "true"); // feat_343 default OFF
			if (key === "autoReplyMediaEnabled") setAutoReplyMediaEnabled(value === "true");
			if (key === "autoReplyMediaScope") setAutoReplyMediaScope(value || "ai_only");
			if (key === "autoReplyMediaActions") {
				setAutoReplyMediaActions(deriveMediaActions(value, null));
			}
			if (key === "autoReplyMediaTexts") {
				try {
					const parsedTexts = value ? JSON.parse(value) : {};
					setAutoReplyMediaTexts(parsedTexts && typeof parsedTexts === "object" ? parsedTexts : {});
				} catch (_) {
					setAutoReplyMediaTexts({});
				}
			}
			if (key === "autoReplyMediaTimeout") setAutoReplyMediaTimeout(value || "60");
			if (key === "emailReportEnabled") setEmailReportEnabled(value === "true");
			if (key === "emailReportRecipients") setEmailReportRecipients(value);
			if (key === "emailReportTriggerQueueIds") setEmailReportTriggerQueueIds(value ? value.split(",").map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n)) : []);
			if (key === "emailReportTimeoutHours") setEmailReportTimeoutHours(value);
			if (key === "emailReportTimerMode") setEmailReportTimerMode(value || "updatedAt");
			if (key === "emailReportSubjectTemplate") setEmailReportSubjectTemplate(value);
			if (key === "emailReportHtmlTemplate") setEmailReportHtmlTemplate(value);
			}
		};
		socket.on("settings", handleSettings);

		// SINGLETON getSocket(): off() por referencia, NUNCA disconnect().
		return () => { socket.off("settings", handleSettings); };
	}, []);

	const fetchContactTypes = () => {
		api.get("/contact-types").then(({ data }) => setContactTypes(data)).catch(() => {});
	};

	const saveSetting = async (key, value) => {
		try {
			await api.put(`/settings/${key}`, { value });
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	// Silenciar IA al tomar el ticket (default off).
	const handleToggleAiSilenceOnTicketOpen = async (checked) => {
		setAiSilenceOnTicketOpen(checked);
		await saveSetting("aiSilenceOnTicketOpen", checked ? "true" : "false");
	};

	// feat_181: admin toggles for Secretaria IA / Vendedor IA.
	// Turning Secretaria off forces Vendedor off (dependency).
	const handleToggleSecretariaIaAdmin = async (checked) => {
		setSecretariaIaEnabled(checked);
		if (!checked) {
			setVendedorIaEnabled(false);
			await Promise.all([
				saveSetting("secretariaIaEnabled", "false"),
				saveSetting("vendedorIaEnabled", "false"),
			]);
		} else {
			await saveSetting("secretariaIaEnabled", "true");
		}
	};

	const handleToggleVendedorIaAdmin = async (checked) => {
		if (!secretariaIaEnabled) return;
		setVendedorIaEnabled(checked);
		await saveSetting("vendedorIaEnabled", checked ? "true" : "false");
	};

	const handleSaveReopenWindow = async (rawValue) => {
		let parsed = parseInt(rawValue, 10);
		if (isNaN(parsed) || parsed < 0) parsed = 1;

		// Respetar el máximo definido por superadmin
		if (ticketReopenWindowMax !== null) {
			const max = parseInt(ticketReopenWindowMax, 10);
			if (!isNaN(max) && parsed > max) {
				parsed = max;
				toast.warning(`El máximo permitido es ${max} días`);
			}
		}

		const finalValue = String(parsed);
		setTicketReopenWindowDays(finalValue);
		await saveSetting("ticketReopenWindowDays", finalValue);
	};

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;
		await saveSetting(settingKey, selectedValue);
	};

	const getFieldValue = (key) => {
		const map = { webhookUrl, webhookToken, webhookTimeout, webhookRetries, webhookPrompt };
		return map[key] !== undefined ? map[key] : "";
	};

	const setFieldValue = (key, value) => {
		const setters = {
			webhookUrl: setWebhookUrl,
			webhookToken: setWebhookToken,
			webhookTimeout: setWebhookTimeout,
			webhookRetries: setWebhookRetries,
			webhookPrompt: setWebhookPrompt,
		};
		if (setters[key]) setters[key](value);
	};

	const handleSaveWebhookSettings = async () => {
		try {
			await Promise.all([
				api.put("/settings/webhookUrl", { value: webhookUrl }),
				api.put("/settings/webhookToken", { value: webhookToken }),
				api.put("/settings/webhookTimeout", { value: webhookTimeout }),
				api.put("/settings/webhookRetries", { value: webhookRetries }),
				api.put("/settings/webhookPrompt", { value: webhookPrompt }),
			]);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleKeyDown = e => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleSaveWebhookSettings();
		}
	};

	// feat_347 — navegación entre tabs, incluida la cross-tab.
	// 1) cambia el tab, 2) refleja el estado en `?tab=` con history.replace (NUNCA
	//    push: un click de tab no tiene que llenar el historial del navegador),
	// 3) recién en el frame siguiente —con el panel ya visible— scrollea al ancla.
	// El doble rAF es lo que garantiza que el panel destino ya esté pintado:
	// scrollIntoView sobre un panel con display:none es un no-op silencioso.
	// ⚠ El pathname se copia TAL CUAL de history.location: cambiarlo rompería el
	// match exacto de ADMIN_ONLY_PATHS que mantiene al superadmin fuera de acá.
	const goToTab = useCallback((tabId, anchorId) => {
		if (!TAB_IDS.includes(tabId)) return;
		setActiveTab(tabId);
		const params = new URLSearchParams(history.location.search);
		params.set("tab", tabId);
		history.replace({
			pathname: history.location.pathname,
			search: `?${params.toString()}`,
		});
		if (anchorId) {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					const el = document.getElementById(anchorId);
					if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
				});
			});
		}
	}, [history]);

	const getSettingValue = key => {
		const found = settings.find(s => s.key === key);
		return found ? found.value : "";
	};

	const handleCreateContactType = async () => {
		if (!ctForm.name.trim()) return;
		try {
			const { data } = await api.post("/contact-types", ctForm);
			setContactTypes(prev => [...prev, data]);
			setCtForm({ name: "", color: "#667eea" });
		} catch (err) { toastError(err); }
	};

	const handleDeleteContactType = async (id) => {
		try {
			await api.delete(`/contact-types/${id}`);
			setContactTypes(prev => prev.filter(ct => ct.id !== id));
		} catch (err) { toastError(err); }
	};

	const handleCreateContactReason = async () => {
		if (!crForm.name.trim()) return;
		try {
			const { data } = await api.post("/contact-reasons", { name: crForm.name });
			setContactReasons(prev => [...prev, data]);
			setCrForm({ name: "" });
		} catch (err) { toastError(err); }
	};

	const handleDeleteContactReason = async (id) => {
		try {
			await api.delete(`/contact-reasons/${id}`);
			setContactReasons(prev => prev.filter(r => r.id !== id));
		} catch (err) { toastError(err); }
	};

	// Determine which webhook fields to show
	// superadmin sees all; when iaEnabled=true, webhookUrl is always visible for company admins
	const visibleWebhookFields = getWebhookFields().filter(f => {
		// feat_347: se eliminó la rama del superadmin (siempre true). /Settings está en
		// ADMIN_ONLY_PATHS y routes/Route.js redirige a TODO superadmin a /superadmin,
		// así que esa rama era inalcanzable por UI.
		if (clientVisibleFields === null) return false; // not loaded yet
		if (iaEnabled && f.key === "webhookUrl") return true;
		return clientVisibleFields.includes(f.key);
	});

	// feat_339 — spinner de carga. Va DESPUÉS de todos los hooks (los useState de
	// arriba, el useContext y los dos useEffect) y ANTES del render real, así que no
	// viola las reglas de hooks. Se reusa el mismo div.root para que quede centrado
	// sobre la ZONA DE CONTENIDO (mismo padding y ancho), no sobre toda la app —
	// para eso ya está BackdropLoading, que acá tapa de más.
	// ⚠ MUI v4: el prop de Box es gridGap, NO gap.
	if (loading) {
		return (
			<div className={classes.root}>
				<Container maxWidth="md">
					<Box
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						py={10}
						gridGap={12}
					>
						<CircularProgress />
						<Typography variant="body2" color="textSecondary">
							{i18n.t("settingsExtra.loadingSettings")}
						</Typography>
					</Box>
				</Container>
			</div>
		);
	}

	// feat_347 — visibilidad de cada tab, calculada acá junto a los gates de sus
	// bloques. Un tab que quedaría VACÍO no se muestra: nunca abrir un tab en blanco.
	// Al sumar o mover un bloque hay que revisar esta tabla.
	const tabIsVisible = {
		// Identidad / Horario comercial / Datos del negocio / Feriados: sin gate.
		negocio: true,
		// Los 7 bloques de bandeja: sin gate.
		bandeja: true,
		// Firma / Outbound typing / Mensajes programados: sin gate.
		mensajes: true,
		// feat_352 — los 3 bloques de IA viven ahora acá. Ninguno de los 8 bloques
		// del tab tiene gate salvo los 2 de IA (iaAdminCanToggle y
		// secretariaIaEnabled): E1/E2/E3, Configuración clásica, Continuidad y
		// "Respuesta automática a media" se ven siempre → el tab nunca abre vacío.
		automatizaciones: true,
		// feat_352 — tab de UN solo bloque, y ese bloque tiene GATE FUERTE: para la
		// mayoría de las empresas la pestaña no se muestra. Es la MISMA condición
		// que el bloque lleva adentro de TabReporteEmail.js — si se cambia una hay
		// que cambiar la otra, o el tab abre en blanco (regla de feat_347).
		"reporte-email":
			getSettingValue("emailReportSuperadminAllowed") === "true" && user.profile === "admin",
		// Tipos de Cliente / Motivos de Contacto: sin gate.
		clasificacion: true,
		// Token de API y Desarrolladores no tienen gate; sólo "Integración IA"
		// depende de visibleWebhookFields.length > 0.
		integraciones: true,
		// Único tab de un solo bloque, y ese bloque está gateado.
		permisos: user.profile === "admin",
	};
	const visibleTabs = TABS.filter(t => tabIsVisible[t.id]);
	// Si el `?tab=` apunta a un tab oculto para este perfil, se cae al default en
	// vez de mostrar una pantalla vacía.
	const currentTab = visibleTabs.some(t => t.id === activeTab) ? activeTab : DEFAULT_TAB;

	// feat_347 — TODO el estado sigue viviendo acá; los tabs son presentación pura y
	// reciben un único prop `ctx`. Sin Context nuevo y sin useMemo: con los 8 paneles
	// montados el re-render es el mismo que tenía la pantalla de scroll único.
	const ctx = {
		classes,
		userProfile: user.profile,
		// helpers compartidos
		saveSetting,
		handleChangeSetting,
		getSettingValue,
		goToTab,
		// Negocio
		businessName, setBusinessName,
		sede1, setSede1,
		sede2, setSede2,
		assistantName, setAssistantName,
		language, setLanguage,
		tone, setTone,
		always247, setAlways247,
		businessHours, setBusinessHours,
		businessAddress, setBusinessAddress,
		businessPhone, setBusinessPhone,
		businessWhatsapp, setBusinessWhatsapp,
		businessEmail, setBusinessEmail,
		businessLocationLink, setBusinessLocationLink,
		businessHoursText, setBusinessHoursText,
		businessTimezone, setBusinessTimezone,
		// Bandeja
		inboxMode, setInboxMode,
		attendingMode, setAttendingMode,
		closedPooledMode, setClosedPooledMode,
		directResolve, setDirectResolve,
		showUserFilter, setShowUserFilter,
		markReadOnOpen, setMarkReadOnOpen,
		tdrYellowThreshold, setTdrYellowThreshold,
		tdrRedThreshold, setTdrRedThreshold,
		// Mensajes
		signatureEnabled, setSignatureEnabled,
		signatureQuoteStyle, setSignatureQuoteStyle,
		outboundTypingEnabled, setOutboundTypingEnabled,
		outboundTypingAiEnabled, setOutboundTypingAiEnabled,
		cancelScheduledOnReply, setCancelScheduledOnReply,
		// IA
		iaAdminCanToggle,
		secretariaIaEnabled,
		vendedorIaEnabled,
		secretariaIaActive,
		vendedorIaActive,
		aiSilenceOnTicketOpen,
		handleToggleSecretariaIaAdmin,
		handleToggleVendedorIaAdmin,
		handleToggleAiSilenceOnTicketOpen,
		iaScheduleEnabled, setIaScheduleEnabled,
		autoReplyMediaEnabled, setAutoReplyMediaEnabled,
		autoReplyMediaScope, setAutoReplyMediaScope,
		autoReplyMediaActions, setAutoReplyMediaActions,
		autoReplyMediaTexts, setAutoReplyMediaTexts,
		autoReplyMediaTimeout, setAutoReplyMediaTimeout,
		// Automatizaciones
		cartAbandonedEnabled, setCartAbandonedEnabled,
		cartAbandonedHours, setCartAbandonedHours,
		cartAbandonedScheduleMode, setCartAbandonedScheduleMode,
		cartAbandonedSchedule, setCartAbandonedSchedule,
		unansweredEnabled, setUnansweredEnabled,
		unansweredHours, setUnansweredHours,
		forgottenEnabled, setForgottenEnabled,
		forgottenHours, setForgottenHours,
		autoCloseEnabled, setAutoCloseEnabled,
		autoCloseHours, setAutoCloseHours,
		autoCloseNotifyMinutes, setAutoCloseNotifyMinutes,
		autoCloseFarewellMessage, setAutoCloseFarewellMessage,
		autoCloseNotifyAI, setAutoCloseNotifyAI,
		ticketReopenWindowDays, setTicketReopenWindowDays,
		ticketReopenWindowMax,
		handleSaveReopenWindow,
		emailReportEnabled, setEmailReportEnabled,
		emailReportRecipients, setEmailReportRecipients,
		emailReportTriggerQueueIds, setEmailReportTriggerQueueIds,
		emailReportTimeoutHours, setEmailReportTimeoutHours,
		emailReportTimerMode, setEmailReportTimerMode,
		emailReportSubjectTemplate, setEmailReportSubjectTemplate,
		emailReportHtmlTemplate, setEmailReportHtmlTemplate,
		queues,
		emailTestRecipient, setEmailTestRecipient,
		emailTestLoading, setEmailTestLoading,
		emailTestResult, setEmailTestResult,
		// Clasificación
		contactTypes,
		ctForm, setCtForm,
		handleCreateContactType,
		handleDeleteContactType,
		setEditingContactType,
		setTypeModalOpen,
		contactReasons,
		crForm, setCrForm,
		handleCreateContactReason,
		handleDeleteContactReason,
		// Integraciones
		showApiToken, setShowApiToken,
		visibleWebhookFields,
		getFieldValue,
		setFieldValue,
		handleKeyDown,
		handleSaveWebhookSettings,
		// Permisos
		userCanDeleteTicket, setUserCanDeleteTicket,
		userCanDeleteMessage, setUserCanDeleteMessage,
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth="md">
				{/* feat_352 — h1 de la pantalla. Antes era un body2: más chico que el
				    subtitle1 de una sección interna, y la pantalla no tenía NINGÚN
				    heading. Jerarquía: h4/h1 (pantalla) > h5/h2 (TabHeader, feat_351)
				    > subtitle1/h3 (SectionHeader). */}
				<Typography variant="h4" component="h1" className={classes.screenTitle} gutterBottom>
					{i18n.t("settings.title")}
				</Typography>

				<Paper elevation={0} square className={classes.tabsHeader}>
					<Tabs
						value={currentTab}
						onChange={(e, next) => goToTab(next)}
						variant="scrollable"
						scrollButtons="auto"
						indicatorColor="primary"
						textColor="primary"
						aria-label={i18n.t("settings.title")}
					>
						{visibleTabs.map(({ id, labelKey }) => (
							<Tab
								key={id}
								value={id}
								className={classes.tab}
								id={`settings-tab-${id}`}
								aria-controls={`settings-tabpanel-${id}`}
								label={i18n.t(labelKey)}
							/>
						))}
					</Tabs>
				</Paper>

				{/* 🔴 LOS 8 PANELES QUEDAN SIEMPRE MONTADOS — se OCULTAN, nunca se
				    desmontan. 26 `onBlur` guardan el valor tipeado al salir del campo:
				    con render condicional (`{tab === "x" && <Tab/>}`) React desmontaría
				    el input al cambiar de tab, el onBlur NO dispararía y el usuario
				    perdería lo escrito SIN NINGÚN AVISO. La pantalla ya montaba los 28
				    bloques a la vez, así que el costo de DOM es el mismo de siempre.
				    NO "optimizar" esto con lazy, keepMounted ni unmount. */}
				{visibleTabs.map(({ id, Component }) => (
					<div
						key={id}
						role="tabpanel"
						id={`settings-tabpanel-${id}`}
						aria-labelledby={`settings-tab-${id}`}
						hidden={currentTab !== id}
						style={{ display: currentTab === id ? "block" : "none" }}
					>
						<Component ctx={ctx} />
					</div>
				))}
			</Container>
		{/* ContactTypeModal sigue montado FUERA del <Container>, igual que antes, y
		    sigue recibiendo signatureFeatureEnabled del estado del padre (el switch
		    de Firma vive en el tab Mensajes). */}
		<ContactTypeModal
			open={typeModalOpen}
			onClose={(shouldReload) => {
				setTypeModalOpen(false);
				setEditingContactType(null);
				if (shouldReload) fetchContactTypes();
			}}
			contactType={editingContactType}
			signatureFeatureEnabled={signatureEnabled}
		/>
		</div>
	);
};

export default Settings;
